import React, { useContext, useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { navigate } from '@reach/router';

// Context
import { AnalyticsContext } from '../analytics/AnalyticsContext';

// Hooks
import useSitePages from '../hooks/useSitePages/useSitePages';
import { useVisitId } from '../hooks/useVisitId';
import { useIsMounted } from '../hooks/useIsMounted';
import useContextToken from '../hooks/useContextToken';

// Components
import {
  Layout,
  FooterSectionCTA,
  HeroBanner,
  MovieDetails,
  SEO,
  CastList,
  ShopBannerCTA,
  Swimlane,
} from '../components';

// Models
import { IMedia, INetwork } from '../utils/models';

// Utils
import { getBuyflowLink } from '../utils/helpers';

// Styles
import './media.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';
import { useOffer } from '../utils/helpers';

let tealiumTrackPageLoad;
if (typeof window !== 'undefined') {
  const { trackPageLoad } = initTealiumTracker({ debugMode: true });
  tealiumTrackPageLoad = trackPageLoad;
}

interface IProps {
  data: any;
  pageContext: {
    id: string;
    tmsId: string;
    network: INetwork;
  };
}

export const MOVIE_QUERY = graphql`
  query ($id: ID!, $tmsId: String!) {
    allSOD {
      # Movie
      asset(id: $id, cacheValue: false) {
        assetType
        id
        ovpUrl
        altIds {
          tmsRootId
        }
        title
        imageUrl
        heroImage {
          childImageSharp {
            gatsbyImageData(
              layout: FULL_WIDTH
              quality: 100
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
        description
        shortDescription
        descriptors {
          moods
        }
        topCast {
          name
          characterName
          personId
          role
        }
        ratings {
          agency
          rating
        }
        genres {
          id
          name
        }
        releaseYear
        duration
        offer {
          offerType
          providerName
          networkImgUrl
          startTime
          endTime
          price {
            currency
            value
          }
        }
        assetTrailer: primaryTrailer {
          mp4
          hls
          dash
        }
      }

      # Similar Assets
      similarAssets(id: $id, limit: "10") {
        ...SimilarAssetsFragment
      }
    }

    # Access Services
    accessItem(tmsId: { eq: $tmsId}) {
      accessServices
    }
   
  }
`;

const Movie = ({ data, pageContext }: IProps) => {
  console.log('Movie page for: ', pageContext);
  if (!data.allSOD.asset || !data.allSOD.asset.altIds) {
    console.log(
      `================NO MOVIE DATA!!!!!!!!!================`,
      pageContext
    );
    console.log(`data: `, JSON.stringify(data.allSOD.asset, null, 2));
    navigate('/404');
    return; 
  }
  
  const { analytics } = useContext(AnalyticsContext);
  const { sitePageMap } = useSitePages();
  const [relatedAssets, setRelatedAssets] = useState<IMedia[]>([]);

  const {
    asset: movie,
    similarAssets: { similarAssets },
  }: { asset: IMedia; similarAssets: { similarAssets: IMedia[] } } =
    data.allSOD;

  const { title, shortDescription, topCast, altIds, description, offer } =
    movie;

  const currentOffer = useOffer(offer);
  const { isMounted } = useIsMounted();

  const isRental = currentOffer.price?.value;
  const { network } = pageContext;

  const pageDescription =
    shortDescription ||
    description ||
    `Watch ${title} with Spectrum On Demand.`;

  const visitId = useVisitId();
  const { contextToken } = useContextToken();

  const buyflowTransactionalMovieShopBanner = getBuyflowLink(
    'https://www.spectrum.com/cable-tv/plans?cmp=sspp_sod_gm_tmp_shopspecplans_unauth_050523',
    'https://www.spectrum.net/change?cid=sspp_sod_gm_tmp_shopspecplans_auth_050523'
  );
  const buyflowIndividualMovieShopBanner = getBuyflowLink(
    'https://www.spectrum.com/cable-tv/plans?cmp=sspp_sod_gm_imp_shopspecplans_unauth_050523',
    'https://www.spectrum.net/change?cid=sspp_sod_gm_imp_shopspecplans_auth_050523'
  );
  const buyflowTransactionalMovieFooterShop = getBuyflowLink(
    'https://www.spectrum.com/cable-tv/plans?cmp=sspp_sod_gm_tmp_shop_unauth_050523',
    'https://www.spectrum.net/change?cid=sspp_sod_gm_tmp_shop_auth_050523'
  );
  const buyflowIndividualMovieFooterShop = getBuyflowLink(
    'https://www.spectrum.com/cable-tv/plans?cmp=sspp_sod_gm_imp_shop_unauth_050523',
    'https://www.spectrum.net/change?cid=sspp_sod_gm_imp_shop_auth_050523'
  );
  const buyflowIndividualMovieFooterSignIn = getBuyflowLink(
    'https://watch.spectrum.net/ondemand?cmp=sod_signin_indvmovie',
    'https://watch.spectrum.net/?cid=sspp_sod_gm_buyflow_watch_auth_050523'
  );

  const header = (
    <HeroBanner trailer={movie.assetTrailer || null}>
      <HeroBanner.HeroImage
        // imageUrl={`${BASE_IMAGE_URI}/imageserver/program/${movie.id}`}
        heroImage={movie.heroImage}
        imageAltText={movie.title}
      />
      <HeroBanner.MediaContent
        media={movie}
        network={network}
        analytics={analytics}
      />
    </HeroBanner>
  );

  // For Related To Swimlane
  useEffect(() => {
    const assetsWithPages = similarAssets.reduce((acc, asset) => {
      // Only include assets that pages have been created for
      const path = sitePageMap?.get(asset.id);
      if (path) {
        acc = [...acc, { ...asset, path }];
      }
      return acc;
    }, [] as IMedia[]);

    setRelatedAssets(assetsWithPages);
  }, [sitePageMap, similarAssets]);

  // Quantum Tracking for page view
  useEffect(() => {
    if (analytics) {
      analytics.trackPageView(`Movie Detail`, {
        currPageAppSection: 'Movie',
        currPageIsLazyLoad: false,
      });
      analytics.trackPageView(`Movie - ${movie.title}`, {
        currPageAppSection: 'Movie',
        currPageIsLazyLoad: false,
      });
    }
  }, [analytics, movie]);

  // Tealium Page Tracking
  useEffect(() => {
    tealiumTrackPageLoad({
      pagename: 'Movie Details Page',
      section: 'Movies',
      pagetype: 'Movie Page',
      sc_reporting_suite: 'charterspectrumondemand',
      movieTitle: title,
      contentId: parseInt(altIds.tmsRootId || '5', 10),
    });
  }, [title, altIds]);

  if (!isMounted) return null;
  return (
    <Layout className="media-page" header={header}>
      <SEO title={title} description={pageDescription} />

      {/* Shop Banner */}
      <ShopBannerCTA
        title="Watch this title and more with Spectrum TV"
        linkPath={
          isRental
            ? `${buyflowTransactionalMovieShopBanner}&visitId=${visitId}${contextToken}`
            : `${buyflowIndividualMovieShopBanner}&visitId=${visitId}${contextToken}`
        }
        tealiumConfig={{
          dataLinkType: 'shop-banner__primary-button',
          dataLinkName: `Shop Banner - Shop Spectrum's Plans`,
        }}
      />

      {/* Details */}
      <MovieDetails media={movie} accessServices={data.accessItem?.accessServices || []} />

      {/* Cast */}
      {topCast?.length ? (
        <section>
          <h2 className="section-title">Cast</h2>
          <CastList cast={topCast} />
        </section>
      ) : null}

      {/* Related To Swimlane */}
      {relatedAssets?.length ? (
        <>
          <hr className="page__divider" aria-hidden={true} />
          <Swimlane
            title={`Related To ${title}`}
            items={relatedAssets}
            analyticsConfig={{
              analytics,
              eventCase:
                'SpectrumOnDemand_selectContent_related_movies_assetSelection',
            }}
          />
        </>
      ) : null}

      {/* Footer */}
      <FooterSectionCTA
        shopLink={
          isRental
            ? `${buyflowTransactionalMovieFooterShop}&visitId=${visitId}${contextToken}`
            : `${buyflowIndividualMovieFooterShop}&visitId=${visitId}${contextToken}`
        }
        signInLink={
          isRental
            ? `https://watch.spectrum.net/ondemand?cmp=sod_signin_tvod&visitId=${visitId}${contextToken}`
            : `${buyflowIndividualMovieFooterSignIn}&visitId=${visitId}${contextToken}`
        }
        isRental={!!isRental}
        analytics={analytics}
      />
    </Layout>
  );
};

export default Movie;
